/**
 * Rebrand: Pandectes cookie banner overrides.
 *
 * The banner renders inside an open shadow root on <pandectes-cmp>, which page stylesheets can't reach.
 * Colours / button radius / border are set through the app's CSS variables in assets/base.css.
 * The rules below have no variable, so they're injected into the shadow root (with !important so they
 * win over the app's own styles):
 * - Buttons: Grano, uppercase, brand hover states (spreadsheet: Cookie consent widget).
 * - Layout: stack the text above the buttons until the banner is 768px wide. Pandectes switches to a
 *   side-by-side layout at 500px, which squashes the text into a ~14px column between ~500–767px.
 */
const STYLE_ID = 'theme-pandectes-overrides';
const MAX_RETRIES = 20;

const CSS = `
  div.actions button {
    font-family: var(--font-heading--family) !important;
    text-transform: uppercase !important;
    letter-spacing: 0.04em !important;
  }

  div.actions button:hover {
    opacity: 1 !important;
  }

  div.actions button.filled:hover {
    background-color: var(--color-brand-poppy-dark) !important;
    border-color: var(--color-brand-poppy-dark) !important;
  }

  div.actions button.outlined:hover {
    background-color: var(--color-brand-poppy) !important;
    color: var(--color-brand-cream) !important;
  }

  @container (width < 768px) {
    .inner.inner-grid {
      grid-template-columns: 1fr !important;
    }

    .inner.inner-grid > .header {
      grid-column: auto !important;
    }

    .inner.inner-grid > .body {
      padding-right: 0 !important;
    }

    .actions.horizontal {
      justify-content: flex-start !important;
    }
  }
`;

/** @param {Element} host */
function inject(host) {
  const root = host.shadowRoot;
  if (!root || root.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  root.appendChild(style);

  // If Pandectes re-renders its shadow root, keep the overrides present and last.
  new MutationObserver(() => {
    if (root.lastElementChild !== style) root.appendChild(style);
  }).observe(root, { childList: true });
}

let retries = 0;

function scan() {
  const hosts = document.querySelectorAll('pandectes-cmp');
  hosts.forEach(inject);

  // The shadow root can be attached a moment after the element appears; retry briefly.
  const pending = [...hosts].some((host) => !host.shadowRoot);
  if (pending && retries < MAX_RETRIES) {
    retries += 1;
    setTimeout(scan, 250);
  }
}

customElements.whenDefined('pandectes-cmp').then(() => {
  scan();
  // Pandectes appends the banner directly to <body>.
  new MutationObserver(scan).observe(document.body, { childList: true });
});
